import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { getStoredUserData } from "@/utils/getStoredUser";
import { useActivityDetail } from "@/features/ActivityDetail/hooks/useActivity";
import Loading from "@/components/fragments/Loadin";

export default function PaymentSuccess() {
  const { outNo } = useParams<{ outNo: string }>();
  const userData = getStoredUserData();
  let email = userData?.email || "";

  const {
    data: transactionResponse,
    isLoading,
    isError,
    error,
  } = useActivityDetail({ email, outNo });

  // Helper functions
  const formatCurrency = (value: string | number) => {
    const numValue = typeof value === "string" ? parseInt(value) : value;
    return `Rp ${numValue.toLocaleString("id-ID")}`;
  };

  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Loading state
  if (isLoading) {
    return <Loading message="Loading..." size="large" fullScreen={true} />;
  }

  // Error state
  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center">
          <div className="flex items-center justify-center w-24 h-24 mx-auto mb-4 bg-red-100 rounded-full">
            <svg
              className="w-12 h-12 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="mb-2 text-2xl font-bold text-gray-800 dark:text-white">
            Gagal Memuat Data
          </h2>
          <p className="mb-6 text-gray-600 dark:text-white/80">
            {error?.message || "Terjadi kesalahan saat memuat data transaksi"}
          </p>
          <Link to="/catalog">
            <Button>Kembali ke Katalog</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Get transaction data
  const transactionData = transactionResponse;

  return (
    <div className="min-h-screen px-4 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Success Icon & Message */}
        <div className="mb-8 text-center duration-500 animate-in fade-in zoom-in">
          <div className="flex items-center justify-center w-24 h-24 mx-auto mb-4 rounded-full shadow-lg bg-linear-to-br from-green-400 to-green-600">
            <svg
              className="text-white w-14 h-14"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-[#1e1e1e] dark:text-white mb-2">
            Pembayaran Berhasil!
          </h1>
          <p className="text-gray-600 dark:text-white/80">
            Terima kasih atas pembelian Anda, {transactionData?.cust_name}
          </p>
        </div>

        {/* Transaction Card */}
        <div className="bg-white dark:bg-[#37393d] rounded-3xl shadow-xl overflow-hidden mb-6 animate-in slide-in-from-bottom duration-700">
          {/* Header */}
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-[#37393d]/70 mb-1 dark:text-white">
                  No. Transaksi
                </p>
                <p className="text-xl font-bold text-[#1e1e1e] dark:text-white">
                  {transactionData.out_no}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-[#37393d]/70 mb-1 dark:text-white">
                  Tanggal
                </p>
                <p className="font-semibold text-[#1e1e1e] dark:text-white">
                  {formatDate(transactionData.out_date)}
                </p>
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div className="px-6 py-4 bg-[#efecff]/50 dark:bg-[#1e1e1e] border-b border-gray-200 dark:border-[#37393d]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#d7d0fe] rounded-full flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-[#37393d]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-white">
                  Pelanggan
                </p>
                <p className="font-semibold text-[#1e1e1e] dark:text-white">
                  {transactionData?.cust_name == "Default"
                    ? "Umum"
                    : transactionData?.cust_name}
                </p>
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="px-6 py-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-600 dark:text-white">
              Detail Belanja
            </h3>
            {transactionData.details.map((item, index) => (
              <div
                key={index}
                className="bg-[#efecfa] dark:bg-[#1e1e1e] rounded-xl p-4 mb-3 hover:bg-[#d7d0fe]/30 transition"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-bold text-[#1e1e1e] mb-1 dark:text-white">
                      {item.mtrl_name}
                    </h4>
                    {item.mtrl_desc && (
                      <p className="mb-2 text-xs leading-relaxed text-gray-600 dark:text-white/80">
                        {item.mtrl_desc}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-white">
                      <span className="px-2 py-1 font-medium bg-white dark:bg-[#37393d] rounded-md">
                        {item.qty} {item.satuan}
                      </span>
                      <span>×</span>
                      <span>{formatCurrency(item.sell_price)}</span>
                    </div>
                  </div>
                  <div className="ml-4 text-right">
                    <p className="font-bold text-[#1e1e1e] dark:text-white">
                      {formatCurrency(item.subtotal)}
                    </p>
                    {item.discount > 0 && (
                      <p className="text-xs text-red-500 line-through">
                        {formatCurrency(item.sell_price * item.qty)}
                      </p>
                    )}
                  </div>
                </div>
                {item.discount > 0 && (
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[#d7d0fe]/50">
                    <span className="text-xs bg-[#ff7075]/10 text-[#ff7075] px-2 py-0.5 rounded-full font-medium">
                      Diskon {formatCurrency(item.discount)}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 dark:bg-[#1e1e1e]">
            <div className="mb-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-white">Subtotal</span>
                <span className="font-semibold dark:text-white">
                  {formatCurrency(transactionData.total_price)}
                </span>
              </div>
              {transactionData.discount_total > 0 && (
                <div className="flex justify-between text-sm text-red-500">
                  <span className="dark:text-red-400">Diskon Total</span>
                  <span className="font-semibold dark:text-red-400">
                    -{formatCurrency(transactionData.discount_total)}
                  </span>
                </div>
              )}
              {transactionData.ppn > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-white">PPN</span>
                  <span className="font-semibold dark:text-white">
                    {formatCurrency(transactionData.ppn)}
                  </span>
                </div>
              )}
            </div>

            <div className="pt-3 mb-3 border-t-2 border-gray-300 border-dashed">
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-[#1e1e1e] dark:text-white">
                  Total Pembayaran
                </span>
                <span className="text-2xl font-bold text-[#1e1e1e] dark:text-white">
                  {formatCurrency(transactionData.grand_total)}
                </span>
              </div>
            </div>

            <div className="bg-[#d7d0fe]/20 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-700 dark:text-white">Tunai</span>
                <span className="font-semibold dark:text-white">
                  {formatCurrency(transactionData.totalbayar)}
                </span>
              </div>
              <div className="flex justify-between items-center border-t border-[#d7d0fe] pt-2">
                <span className="font-medium text-gray-700 dark:text-white">
                  Kembalian
                </span>
                <span
                  className={`text-xl font-bold ${
                    transactionData.kembalian >= 0
                      ? "text-green-600 dark:text-green-400"
                      : "text-red-600 dark:text-red-400"
                  }`}
                >
                  {formatCurrency(Math.abs(transactionData.kembalian))}
                  {transactionData.kembalian < 0 && " (Kurang)"}
                </span>
              </div>
            </div>
          </div>

          {/* Status Badge */}
          <div className="px-6 py-4 bg-white dark:bg-[#37393d]">
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm font-semibold text-green-700 dark:text-green-400">
                Status: {transactionData.status}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 sm:flex-row">
          <Link to="/catalog" replace className="w-full sm:flex-1">
            <Button
              variant="secondary"
              className="w-full h-12 font-semibold border border-gray-200 cursor-pointer"
            >
              Transaksi Baru
            </Button>
          </Link>
          <Link
            to={`/struk/${transactionData.out_no}`}
            className="w-full sm:flex-1"
          >
            <Button
              variant="default"
              className="w-full h-12 font-semibold text-gray-900 border border-gray-200 cursor-pointer dark:text-white"
            >
              Cetak Struk
            </Button>
          </Link>
        </div>

        {/* Footer Note */}
        <p className="mt-6 text-sm text-center text-gray-500 dark:text-white/60">
          Simpan struk ini sebagai bukti pembayaran yang sah
        </p>
      </div>
    </div>
  );
}
